/* eslint-disable @typescript-eslint/no-explicit-any */
import { PortalActions } from "@design-system/utils/portals";
import { ReactAtom } from "@mongez/react-atom";
import { useNavigation } from "@remix-run/react";

import { cn } from "@design-system/utils";
import { useEffect, useId, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { CloseButton } from "../Button/CloseButton";
import { LoaderIcon } from "../Icons";

const widthMap = {
  sm: "w-[500px]",
  md: "w-[800px]",
  lg: "w-[1000px]",
  xl: "w-[1200px]",
  "fit-content": "w-fit",
  "full-screen": "w-full h-full max-h-full",
};

const radiusMap = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
  "3xl": "rounded-3xl",
};

export type PortalProps = {
  portal: ReactAtom<any, PortalActions>;
  clickOutside?: boolean;
  closeButton?: boolean;
  esc?: boolean;
  width?: keyof typeof widthMap | number;
  radius?: keyof typeof radiusMap;
  center?: boolean;
  overlay?: boolean;
  zIndex?: number;
  className?: string;
  children: React.ReactNode;
  isLoading?: boolean;
  onClose?: () => void;
  keepMounted?: boolean;
  classes?: {
    root?: string;
    overlay?: string;
    content?: string;
    closeButton?: string;
  };
};

export function Portal({
  zIndex = 1000,
  portal,
  children,
  classes = {},
  overlay = true,
  clickOutside = true,
  isLoading = false,
  closeButton = true,
  width = "lg",
  esc = true,
  onClose,
  keepMounted = false,
  radius = "xl",
  center = true,
}: PortalProps) {
  const opened = portal.useOpened();
  const ref = useRef<HTMLDivElement | null>(null);
  const navigation = useNavigation();

  const id = useId();

  useEffect(() => {
    if (!opened || navigation.state !== "loading") return;

    // close on navigation
    portal.close();
  }, [navigation.state, opened, portal]);

  useEffect(() => {
    if (esc) {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          portal.close();
        }
      };

      document.addEventListener("keydown", handleEscape);

      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [esc]);

  useEffect(() => {
    if (opened) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      onClose?.();
    }
  }, [opened]);

  const closeOverlay = () => {
    if (!clickOutside) return;

    portal.close();
  };

  return (
    <>
      {opened && (
        <div
          style={{ zIndex }}
          className={twMerge(
            "portal fixed inset-0 flex p-2",
            center && "items-center justify-center",
            width === "full-screen" && "p-0",
            classes.root,
          )}
          id={`portal-${id}`}
          ref={ref as any}
        >
          {overlay && (
            <div
              onClick={closeOverlay}
              aria-hidden="true"
              className={twMerge(
                `portal-overlay fixed inset-0 z-50 h-full w-full bg-gray-900/80 dark:bg-gray-900/80`,
                classes.overlay,
              )}
            />
          )}
          <div
            className={twMerge(
              "portal-content relative z-50 mx-auto flex max-w-full gap-6 bg-background dark:bg-gray-800 p-4",
              typeof width === "number" ? `w-[${width}px]` : widthMap[width],
              width !== "full-screen" && radiusMap[radius],
              width === "full-screen" ? "h-full" : "max-h-[90vh]",
              classes.content,
            )}
          >
            <div className="w-full">
              {keepMounted === true ? children : opened && children}
            </div>
            {closeButton && (
              <CloseButton
                onClick={portal.close}
                className={cn(
                  "absolute right-5 top-5 h-[40px] w-fit rounded-full",
                  classes.closeButton,
                )}
              />
            )}
          </div>
          {isLoading && (
            <div className="bg-dark absolute inset-0 z-50 flex items-center justify-center bg-opacity-70">
              <LoaderIcon className="h-8 w-8 animate-spin" />
            </div>
          )}
        </div>
      )}
    </>
  );
}
